import { useEffect, useState } from 'react';

export interface SectionNavItem {
  id: string;
  label: string;
}

export interface SectionNavProps {
  items: ReadonlyArray<SectionNavItem>;
  /** sticky 상단 offset (PageHeaderSticky 높이 보정). 기본 88. */
  topOffset?: number;
}

/**
 * 고도몰 패턴 — 좌측 sticky anchor nav.
 * IntersectionObserver로 현재 viewport에 들어온 섹션을 active 표시.
 * click → smooth scroll.
 */
export const SectionNav = ({ items, topOffset = 88 }: SectionNavProps) => {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibility = new Map<string, number>();

    const onIntersect: IntersectionObserverCallback = (entries) => {
      for (const entry of entries) {
        visibility.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
      }
      let bestId = items[0]?.id ?? '';
      let bestRatio = -1;
      for (const item of items) {
        const ratio = visibility.get(item.id) ?? 0;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestId = item.id;
        }
      }
      if (bestRatio > 0) setActiveId(bestId);
    };

    const observer = new IntersectionObserver(onIntersect, {
      rootMargin: `-${topOffset + 40}px 0px -55% 0px`,
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    for (const item of items) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }
    observers.push(observer);

    return () => {
      observers.forEach((o) => o.disconnect());
    };
  }, [items, topOffset]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - topOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveId(id);
  };

  return (
    <nav
      aria-label="섹션 목차"
      className="sticky flex flex-col gap-1 rounded-lg p-3"
      style={{
        top: topOffset,
        background: 'var(--surface-raised)',
        border: '1px solid var(--divider-strong)',
        width: 220,
        alignSelf: 'flex-start',
      }}
    >
      <p
        className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide"
        style={{ color: 'var(--content-muted)' }}
      >
        섹션 목차
      </p>
      <ol className="flex flex-col gap-0.5">
        {items.map((item, idx) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => scrollTo(item.id)}
                aria-current={active ? 'true' : undefined}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors"
                style={{
                  background: active ? 'var(--surface-1)' : 'transparent',
                  color: active ? 'var(--content-strong)' : 'var(--content-default)',
                  fontWeight: active ? 600 : 400,
                  borderLeft: active
                    ? '2px solid var(--cta-primary)'
                    : '2px solid transparent',
                }}
              >
                <span
                  className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] tabular-nums"
                  style={{
                    background: active ? 'var(--cta-primary)' : 'var(--surface-1)',
                    color: active ? 'var(--cta-primary-on)' : 'var(--content-muted)',
                    fontWeight: 600,
                  }}
                >
                  {idx + 1}
                </span>
                <span className="truncate">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
